import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { faKey } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { UsersService } from 'src/app/services/users.service';
import { AuthenticationService } from '../../services/authentication.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { MatCard } from '@angular/material/card';
import { environment } from 'src/environments/environment';
import { SettingsService } from 'src/app/services/settings.service';
import { ToastrService } from 'ngx-toastr';
import { ExternalParameters } from 'src/app/models/external-parameters';
import { ExternalResponse } from 'src/app/models/external-response';
import { switchMap, tap } from 'rxjs';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    imports: [MatCard, ReactiveFormsModule, FaIconComponent, MatFormField, MatLabel, MatInput, MatError, MatIconButton, MatSuffix, MatIcon, MatButton, TranslateModule]
})
export class LoginComponent implements OnInit {
    private destroyRef = inject(DestroyRef);

    loginForm: UntypedFormGroup;
    hide = true;
    authenticationInvalid = false;
    version: string = environment.version;

    // Icône clé
    public fakey = faKey;

    // Délai de prévenance de l'expiration de mot de passe en jours
    private delayNotice: number = 15;

    constructor(
        private router: Router,
        private formBuilder: UntypedFormBuilder,
        private authenticationService: AuthenticationService,
        private userService: UsersService,
        private organizationsService: OrganizationsService,
        private spinner: NgxSpinnerService,
        private settingsService: SettingsService,
        private toastrService: ToastrService,
        private translateService: TranslateService,
        private activatedRoute: ActivatedRoute,
        private instanceService: InstanceService
    ) {
        if (this.activatedRoute.snapshot.queryParams['response']) {
            const response = JSON.parse(this.activatedRoute.snapshot.queryParams['response']) as ExternalResponse;

            if (response) {
                if (response.code === 200) {
                    this.toastrService.success(response.message, '', { enableHtml: true });
                } else {
                    this.toastrService.warning(response.message, '', { enableHtml: true });
                }
                this.router.navigate([]);
            }
        }

        this.instanceService.currentInstance.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((instance) => {
            this.checkPasswordExpiration();
            this.spinner.hide();

            if (instance) {
                this.router.navigate(['/dashboard']);
            } else {
                this.router.navigate(['/instance']);
            }
        });
    }

    ngOnInit() {
        this.loginForm = this.formBuilder.group({
            username: ['', Validators.required],
            password: ['', Validators.required],
        });
    }

    onSubmit() {
        console.log('onsubmit');
        this.spinner.show();

        this.authenticationService
            .login(this.loginForm.get('username').value, this.loginForm.get('password').value)
            .pipe(
                switchMap((token) => {
                    console.log('Token created:' + token.accessToken);
                    this.authenticationService.setCurrentToken(token);
                    if (token.passwordExpireOn) {
                        sessionStorage.setItem('passwordExpireOn', token.passwordExpireOn.toString());
                    }
                    return this.userService.getCurrentUser().pipe(
                        switchMap((user) => {
                            console.log('User found:' + user.display);
                            this.userService.setSessionCurrentUser(user);
                            return this.organizationsService.getOrganisation(user.organizationId).pipe(
                                tap((organization) => {
                                    this.organizationsService.setCurrentOrganization(organization);
                                })
                            );
                        })
                    );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.router.navigate(['/dashboard']);
                },
                error: () => {
                    this.authenticationInvalid = false;
                    this.spinner.hide();
                },
            });
    }

    forgottenPassword() {
        this.spinner.show();

        this.settingsService
            .getDomainSetting()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (x) => {
                    let gumUrl: string;
                    try {
                        gumUrl = x.apps['gum']['url'];
                    } catch {
                        gumUrl = null;
                    }

                    if (gumUrl) {
                        let parameters = new ExternalParameters();
                        parameters.name = 'Centrex UI';
                        parameters.return = `${window.location.origin}/login`;
                        window.location.href = `${gumUrl}/forgottenpassword?parameters=${JSON.stringify(parameters)}`;
                    } else {
                        this.toastrService.warning(this.translateService.instant('Login.DomainWarning'), '', { enableHtml: true });
                    }
                },
                error: () => {
                    this.toastrService.warning(this.translateService.instant('Login.DomainWarning'), '', { enableHtml: true });
                },
                complete: () => {
                    this.spinner.hide();
                },
            });
    }

    private checkPasswordExpiration() {
        const passwordExpireOn = sessionStorage.getItem('passwordExpireOn');
        sessionStorage.removeItem('passwordExpireOn');

        if (passwordExpireOn) {
            const passwordExpireDate = new Date(passwordExpireOn);

            if (passwordExpireDate.getFullYear() > 1) {
                let limiteDate = new Date(passwordExpireOn);
                limiteDate.setDate(passwordExpireDate.getDate() - this.delayNotice);

                const today = new Date();
                // today.setDate(today.getDate() + 140);

                if (today > limiteDate) {
                    this.toastrService.info(this.translateService.instant('Login.MessagePasswordExpire', { days: this.DaysBetweenTwoDate(today, passwordExpireDate) }), '', {
                        enableHtml: true,
                    });
                }
            }
        }
    }

    DaysBetweenTwoDate(dateDebut: Date, dateFin: Date): number {
        const uneJourneeEnMillisecondes = 24 * 60 * 60 * 1000; // Nombre de millisecondes dans une journée
        const differenceEnMillisecondes = dateFin.getTime() - dateDebut.getTime();
        const nombreDeJours = Math.floor(differenceEnMillisecondes / uneJourneeEnMillisecondes);
        return nombreDeJours;
    }
}
